/**
 * Advisor Bar functionality
 * Handles the advisor bar display and updates based on advisor selection
 * Integrated with existing advisor system from advisor-selector.js
 */

class AdvisorBar {
  constructor() {
    this.bar = document.querySelector('advisor-bar');
    this.container = document.querySelector('.advisor-bar__container');
    this.message = document.querySelector('.advisor-bar__message');
    this.selectedMessage = document.querySelector('.advisor-bar__selected-message');
    this.button = document.querySelector('.advisor-bar__button');
    this.editButton = null;
    this.dropdown = null;
    this.modal = null;

    this.isDropdownOpen = false;

    this.init();
  }

  init() {
    if (window.theme && window.theme.hideAdvisorSelection) return;
    if (!this.bar) return;

    this.updateFromStorage();
    this.bindEvents();
    this.createModal();
  }

  getEditText() {
    // Check if we're in a Chinese locale context
    const currentLocale = document.documentElement.lang || document.documentElement.getAttribute('data-locale') || 'en';
    
    // Check for Traditional Chinese locale
    if (currentLocale === 'zh-TW') {
      return '編輯';
    }
    
    // Default to English
    return 'Edit';
  }

  getChangeAdvisorText() {
    const currentLocale = document.documentElement.lang || document.documentElement.getAttribute('data-locale') || 'en';
    if (currentLocale === 'zh-TW') {
      return '變更顧問';
    }
    return 'Change advisor';
  }

  getRemoveAdvisorText() {
    const currentLocale = document.documentElement.lang || document.documentElement.getAttribute('data-locale') || 'en';
    if (currentLocale === 'zh-TW') {
      return '移除顧問';
    }
    return 'Remove advisor';
  }

  bindEvents() {
    // Listen for advisor selection events
    document.addEventListener('advisor:selected', (e) => {
      const { id, name, profileImg } = e.detail;
      this.updateDisplay(id, name, profileImg);
    });

    // Listen for advisor cleared events
    document.addEventListener('advisor:cleared', () => {
      this.clearDisplay();
    });

    // Listen for cart updates that might affect advisor data
    document.addEventListener('cart:updated', () => {
      this.updateFromStorage();
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (this.dropdown && !this.dropdown.contains(e.target) && !this.editButtonContainer?.contains(e.target)) {
        this.closeDropdown();
      }
    });
  }

  updateFromStorage() {
    // Get advisor data with fallback from localStorage when cart attributes are empty
    let advisorId = '';
    let advisorName = '';
    let advisorProfilePicture = '';
    
    // Try to get from cart attributes first (if available)
    if (typeof CartJS !== 'undefined' && CartJS.cart && CartJS.cart.attributes) {
      advisorId = CartJS.cart.attributes['Advisor ID'] || '';
      advisorName = CartJS.cart.attributes['Advisor Name'] || '';
      // advisorProfilePicture = CartJS.cart.attributes['Advisor Profile Picture'] || '';
    }
    
    // If cart attributes are empty, null, undefined, or "-", fallback to localStorage
    if (!advisorId || advisorId === '-' || advisorId === 'null' || advisorId === 'undefined') {
      advisorId = localStorage.getItem('Advisor ID') || '';
    }
    if (!advisorName || advisorName === '-' || advisorName === 'null' || advisorName === 'undefined') {
      advisorName = localStorage.getItem('Advisor Name') || '';
    }
    if (!advisorProfilePicture || advisorProfilePicture === '-' || advisorProfilePicture === 'null' || advisorProfilePicture === 'undefined') {
      advisorProfilePicture = localStorage.getItem('Advisor Profile Picture') || '';
    }

    if (advisorId && advisorName) {
      this.updateDisplay(advisorId, advisorName, advisorProfilePicture);
    } else {
      this.clearDisplay();
    }
  }

  updateDisplay(id, name, profilePicture) {
    console.log('updateDisplay...', this.selectedMessage, this.container);
    if (!this.container || !this.selectedMessage) return;

    // Add the advisor-selected class to container
    this.container.classList.add('advisor-selected');

    // Update the selected message content
    let profileImgHtml = '';
    if (profilePicture && profilePicture !== '' && profilePicture !== '-') {
      profileImgHtml = `<div class="advisor-bar__profile-picture"><img src="${profilePicture}" alt="${name}" width="32" height="32" loading="lazy"></div>`;
    }
    console.log('profileImgHtml', profileImgHtml);

    // Replace [advisor_name] placeholder with actual advisor name
    const messageText = this.selectedMessage.getAttribute('data-original-message') || '';
    const updatedMessage = messageText.replace('[advisor_name]', `${profileImgHtml}${name}`);

    this.selectedMessage.innerHTML = `
      <div class="advisor-bar__message-content">${updatedMessage}</div>
    `;

    // Add edit button if it doesn't exist
    this.addEditButton();
  }

  addEditButton() {
    if (this.editButton) return;

    // Create edit button container with relative positioning
    this.editButtonContainer = document.createElement('div');
    this.editButtonContainer.className = 'advisor-bar__edit-container';
    this.editButtonContainer.style.position = 'relative';
    this.editButtonContainer.style.display = 'inline-block';

    // Create edit button
    this.editButton = document.createElement('button');
    this.editButton.className = 'advisor-bar__edit-button';
    this.editButton.innerHTML = `
      <span class="advisor-bar__edit-text">${this.getEditText()}</span>
    `;

    // Create dropdown
    this.dropdown = document.createElement('div');
    this.dropdown.className = 'advisor-bar__dropdown';
    this.dropdown.innerHTML = `
      <div class="advisor-bar__dropdown-content">
        <button class="advisor-bar__dropdown-item advisor-bar__change-button" data-action="change">
          ${this.getChangeAdvisorText()}
        </button>
        <button class="advisor-bar__dropdown-item advisor-bar__remove-button" data-action="remove">
          ${this.getRemoveAdvisorText()}
        </button>
      </div>
    `;

    // Add edit button and dropdown to container
    this.editButtonContainer.appendChild(this.editButton);
    this.editButtonContainer.appendChild(this.dropdown);

    // Insert edit button container after selected message
    this.selectedMessage.parentNode.insertBefore(this.editButtonContainer, this.selectedMessage.nextSibling);

    // Bind edit button events
    this.editButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleDropdown();
    });

    // Bind dropdown events
    this.dropdown.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (action === 'remove') {
        this.showRemoveConfirmation();
      } else if (action === 'change') {
        window.location.href = "/pages/choose-from-your-nearest-advisors";
      }
    });
  }

  toggleDropdown() {
    if (this.isDropdownOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  openDropdown() {
    if (!this.dropdown || !this.editButton) return;

    this.dropdown.classList.add('is-active');
    this.editButton.classList.add('is-active');
    this.isDropdownOpen = true;
  }

  closeDropdown() {
    if (!this.dropdown || !this.editButton) return;

    this.dropdown.classList.remove('is-active');
    this.editButton.classList.remove('is-active');
    this.isDropdownOpen = false;
  }

  createModal() {
    this.modal = document.getElementById(`advisor-bar__modal`);
    // Create confirmation modal
    // this.modal = document.createElement('div');
    // this.modal.className = 'advisor-bar__modal';
    // this.modal.innerHTML = `
    //   <div class="advisor-bar__modal-overlay"></div>
    //   <div class="advisor-bar__modal-content">
    //     <div class="advisor-bar__modal-header">
    //       <h3>Are you sure you want to remove your Advisor?</h3>
    //       <button class="advisor-bar__modal-close" type="button">
    //         <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
    //           <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
    //         </svg>
    //       </button>
    //     </div>
    //     <div class="advisor-bar__modal-body">
    //       <p>Removing your advisor means your TM7™ will not qualify for <strong>free shipping</strong>.</p>
    //     </div>
    //     <div class="advisor-bar__modal-actions">
    //       <button class="advisor-bar__modal-button advisor-bar__modal-button--secondary" data-action="keep">
    //         Keep advisor
    //       </button>
    //       <button class="advisor-bar__modal-button advisor-bar__modal-button--primary" data-action="remove">
    //         Remove advisor
    //       </button>
    //     </div>
    //     <div class="advisor-bar__modal-footer">
    //       <p>Want to <a href="/pages/choose-from-your-nearest-advisors">change</a> your Advisor instead?</p>
    //     </div>
    //   </div>
    // `;

    // Add modal to body
    // document.body.appendChild(this.modal);

    // Bind modal events
    this.modal.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      const isOverlay = e.target.classList.contains('advisor-bar__modal-overlay');
      const isClose = e.target.closest('.advisor-bar__modal-close');

      if (isOverlay || isClose) {
        this.hideModal();
      } else if (action === 'keep') {
        this.hideModal();
      } else if (action === 'remove') {
        this.removeAdvisor();
      }
    });
  }

  showRemoveConfirmation() {
    console.log('showRemoveConfirmation...');
    this.closeDropdown();
    this.showModal();
  }

  showModal() {
    console.log('showModal...', this.modal);
    if (this.modal) {
      this.modal.classList.add('is-active');
      document.body.classList.add('advisor-bar__modal-open');
    }
  }

  hideModal() {
    if (this.modal) {
      this.modal.classList.remove('is-active');
      document.body.classList.remove('advisor-bar__modal-open');
    }
  }

  removeAdvisor() {
    // Use the existing clearAdvisorData function if available
    if (typeof clearAdvisorData === 'function') {
      clearAdvisorData();
    } else {
      // Fallback implementation
      localStorage.removeItem('Advisor ID');
      localStorage.removeItem('Advisor Name');
      localStorage.removeItem('Advisor Branch Manager Name');
      localStorage.removeItem('Advisor Company Name');
      localStorage.removeItem('Advisor Profile Picture');
      localStorage.removeItem('Advisor Storepoint ID');

      // Clear cart attributes
      if (typeof CartJS !== 'undefined') {
        CartJS.setAttributes({
          'Advisor ID': '',
          'Advisor Name': ''
          // 'Advisor Profile Picture': '-'
        });
      }

      // Dispatch advisor:cleared event
      document.dispatchEvent(new CustomEvent('advisor:cleared'));
    }

    this.hideModal();
  }

  clearDisplay() {
    if (!this.container) return;

    // Remove the advisor-selected class
    this.container.classList.remove('advisor-selected');

    // Remove edit button and dropdown
    if (this.editButton) {
      this.editButton.remove();
      this.editButton = null;
    }
    if (this.dropdown) {
      this.dropdown.remove();
      this.dropdown = null;
    }

    // Reset the selected message to original content
    if (this.selectedMessage) {
      // Get the original message from the data attribute or reset to default
      const originalMessage = this.selectedMessage.getAttribute('data-original-message') || 
                            this.selectedMessage.textContent || 
                            this.selectedMessage.innerText || '';
      
      this.selectedMessage.innerHTML = originalMessage;
    }
  }
}

// Initialize advisor bar when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.advisorBarInstance = new AdvisorBar();
});

// Export for use in other modules
window.AdvisorBar = AdvisorBar;
